import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Alert,
  I18nManager,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { AlarmRule, alarmRulesApi, AlarmRuleTemplate } from '../services/alarmRulesApi';
import { getErrorMessage, handleError, parseError } from '../utils/errorHandler';
import logger from '../utils/logger';
import { canManageDevices } from '../utils/permissions';
import { useHeaderHeight } from '@react-navigation/elements';

interface AlarmRulesScreenProps {
  route: {
    params: {
      deviceId: string;
      deviceName: string;
    };
  };
  navigation: any;
}

export default function AlarmRulesScreen({ route, navigation }: AlarmRulesScreenProps) {
  // Header is transparent (see stack navigator) — push content below it
  const headerHeight = useHeaderHeight();
  // P2: Validate route params
  if (!route?.params?.deviceId) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <Text style={{ fontSize: 16, marginBottom: 20 }}>Missing device information</Text>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Text style={{ color: '#007AFF' }}>Go Back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const { deviceId, deviceName } = route.params;
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { user } = useAuth();
  const userCanManageDevices = canManageDevices(user?.roles || []);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [rules, setRules] = useState<AlarmRule[]>([]);
  const [templates, setTemplates] = useState<AlarmRuleTemplate[]>([]);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);

  const isArabic = i18n.language === 'ar';
  const isRTL = I18nManager.isRTL || isArabic;

  useEffect(() => {
    navigation.setOptions({
      title: isArabic ? 'قواعد الإنذار' : 'Alarm Rules',
      headerTintColor: '#ffffff',
    });
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const rulesData = await alarmRulesApi.getDeviceRules(deviceId);
      setRules(rulesData);
    } catch (error) {
      setLoadError(true);
      handleError(error, 'AlarmRules.loadRules', {
        showAlert: false,
        language: isArabic ? 'ar' : 'en',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const templatesData = await alarmRulesApi.getTemplates();
      setTemplates(templatesData);
      setShowTemplatesModal(true);
    } catch (error) {
      handleError(error, 'AlarmRules.loadTemplates', {
        language: isArabic ? 'ar' : 'en',
      });
    }
  };

  const getRuleTypeLabel = (type: string) => {
    if (type.includes('temperature')) return isArabic ? 'درجة الحرارة' : 'Temperature';
    if (type.includes('humidity')) return isArabic ? 'الرطوبة' : 'Humidity';
    if (type.includes('battery')) return isArabic ? 'البطارية' : 'Battery';
    if (type.includes('level')) return isArabic ? 'المستوى' : 'Level';
    return type;
  };

  const getRuleUnit = (type: string) => {
    if (type.includes('temperature')) return '°C';
    if (type.includes('humidity')) return '%';
    if (type.includes('battery')) return 'V';
    if (type.includes('level')) return 'cm';
    return '';
  };

  const applyTemplate = async (templateId: string) => {
    try {
      const result = await alarmRulesApi.applyTemplate(deviceId, templateId);
      setShowTemplatesModal(false);
      Alert.alert(
        isArabic ? 'نجح' : 'Success',
        isArabic
          ? `تم تطبيق قالب "${result.template}" بنجاح! (${result.rulesCreated} قواعد)`
          : `Template "${result.template}" applied successfully! (${result.rulesCreated} rules)`,
        [{ text: isArabic ? 'موافق' : 'OK', onPress: loadRules }],
      );
    } catch (error) {
      const appError = parseError(error);
      Alert.alert(isArabic ? 'خطأ' : 'Error', getErrorMessage(appError, isArabic ? 'ar' : 'en'));
    }
  };

  const deleteRule = async (ruleId: string) => {
    logger.log('🗑️ Delete button clicked for rule:', ruleId);

    // استخدام window.confirm للويب و Alert.alert للموبايل
    const confirmDelete =
      Platform.OS === 'web'
        ? window.confirm(isArabic ? 'هل تريد حذف هذه القاعدة؟' : 'Delete this rule?')
        : await new Promise((resolve) => {
            Alert.alert(
              isArabic ? 'تأكيد الحذف' : 'Confirm Delete',
              isArabic ? 'هل تريد حذف هذه القاعدة؟' : 'Delete this rule?',
              [
                {
                  text: isArabic ? 'إلغاء' : 'Cancel',
                  style: 'cancel',
                  onPress: () => resolve(false),
                },
                {
                  text: isArabic ? 'حذف' : 'Delete',
                  style: 'destructive',
                  onPress: () => resolve(true),
                },
              ],
            );
          });

    if (!confirmDelete) {
      logger.log('❌ Delete cancelled by user');
      return;
    }

    try {
      logger.log('🔄 Starting delete request for rule:', ruleId);
      await alarmRulesApi.deleteRule(ruleId);
      logger.log('✅ Delete successful, updating UI');
      setRules(rules.filter((r) => r.id !== ruleId));

      // رسالة النجاح
      if (Platform.OS === 'web') {
        alert(isArabic ? 'تم الحذف بنجاح' : 'Deleted successfully');
      } else {
        Alert.alert(
          isArabic ? 'نجح' : 'Success',
          isArabic ? 'تم الحذف بنجاح' : 'Deleted successfully',
        );
      }
    } catch (error) {
      logger.error('❌ Delete failed:', error);
      const appError = parseError(error);
      const errorMsg = getErrorMessage(appError, isArabic ? 'ar' : 'en');

      if (Platform.OS === 'web') {
        alert(`${isArabic ? 'خطأ' : 'Error'}: ${errorMsg}`);
      } else {
        Alert.alert(isArabic ? 'خطأ' : 'Error', errorMsg);
      }
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return theme.colors.error;
      case 'warning':
        return theme.colors.warning;
      default:
        return theme.colors.info;
    }
  };

  const getSeverityBgColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return theme.colors.error + '20';
      case 'warning':
        return theme.colors.warning + '20';
      default:
        return theme.colors.info + '20';
    }
  };

  if (loading) {
    return (
      <View style={[styles.centered, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  if (loadError) {
    return (
      <View style={[styles.centered, { backgroundColor: theme.colors.background }]}>
        <Ionicons
          name="alert-circle-outline"
          size={48}
          color={theme.colors.error}
          style={{ marginBottom: 12 }}
        />
        <Text
          style={{ color: theme.colors.text, fontSize: 16, textAlign: 'center', marginBottom: 16 }}
        >
          {isArabic ? 'فشل تحميل قواعد الإنذار' : 'Failed to load alarm rules'}
        </Text>
        <TouchableOpacity
          onPress={loadRules}
          style={[styles.addButton, { backgroundColor: theme.colors.primary }]}
        >
          <Text style={styles.addButtonText}>{isArabic ? 'إعادة المحاولة' : 'Retry'}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View
      style={[
        styles.container,
        { backgroundColor: theme.colors.background, paddingTop: headerHeight },
      ]}
    >
      <ScrollView>
        {/* Header */}
        <View style={[styles.header, { backgroundColor: theme.colors.card }]}>
          <View>
            <Text style={[styles.deviceName, { color: theme.colors.primary }]}>{deviceName}</Text>
            <Text style={[styles.rulesCount, { color: theme.colors.textSecondary }]}>
              {isArabic ? `${rules.length} قواعد نشطة` : `${rules.length} Active Rules`}
            </Text>
          </View>
          {userCanManageDevices && (
            <TouchableOpacity
              style={[styles.addButton, { backgroundColor: theme.colors.primary }]}
              onPress={loadTemplates}
            >
              <Ionicons name="add" size={24} color="#ffffff" />
              <Text style={styles.addButtonText}>{isArabic ? 'تطبيق قالب' : 'Apply Template'}</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Rules List */}
        {rules.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons
              name="notifications-off-outline"
              size={64}
              color={theme.colors.textSecondary}
            />
            <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
              {isArabic ? 'لا توجد قواعد إنذار' : 'No alarm rules'}
            </Text>
            <TouchableOpacity
              style={[styles.emptyButton, { backgroundColor: theme.colors.primary }]}
              onPress={loadTemplates}
            >
              <Text style={styles.emptyButtonText}>
                {isArabic ? 'تطبيق قالب جاهز' : 'Apply Template'}
              </Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.rulesList}>
            {rules.map((rule) => (
              <View
                key={rule.id}
                style={[
                  styles.ruleCard,
                  { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
                ]}
              >
                <View style={styles.ruleHeader}>
                  <View style={styles.ruleInfo}>
                    <View
                      style={[
                        styles.severityBadge,
                        {
                          backgroundColor: getSeverityBgColor(rule.severity),
                        },
                      ]}
                    >
                      <Text
                        style={[styles.severityText, { color: getSeverityColor(rule.severity) }]}
                      >
                        {rule.severity.toUpperCase()}
                      </Text>
                    </View>
                    <Text style={[styles.ruleType, { color: theme.colors.text }]}>
                      {getRuleTypeLabel(rule.type)}
                    </Text>
                  </View>
                  {userCanManageDevices && (
                    <TouchableOpacity
                      onPress={() => deleteRule(rule.id)}
                      style={styles.deleteButton}
                    >
                      <Ionicons name="trash-outline" size={24} color={theme.colors.error} />
                    </TouchableOpacity>
                  )}
                </View>

                <View style={styles.ruleDetails}>
                  {rule.thresholdHigh !== undefined && rule.thresholdHigh !== null && (
                    <View style={styles.threshold}>
                      <Ionicons name="arrow-up" size={16} color={theme.colors.error} />
                      <Text style={[styles.thresholdLabel, { color: theme.colors.textSecondary }]}>
                        {isArabic ? 'الحد الأعلى:' : 'High:'}
                      </Text>
                      <Text style={[styles.thresholdValue, { color: theme.colors.text }]}>
                        {rule.thresholdHigh}
                        {getRuleUnit(rule.type)}
                      </Text>
                    </View>
                  )}
                  {rule.thresholdLow !== undefined && rule.thresholdLow !== null && (
                    <View style={styles.threshold}>
                      <Ionicons name="arrow-down" size={16} color={theme.colors.info} />
                      <Text style={[styles.thresholdLabel, { color: theme.colors.textSecondary }]}>
                        {isArabic ? 'الحد الأدنى:' : 'Low:'}
                      </Text>
                      <Text style={[styles.thresholdValue, { color: theme.colors.text }]}>
                        {rule.thresholdLow}
                        {getRuleUnit(rule.type)}
                      </Text>
                    </View>
                  )}
                  <View style={styles.threshold}>
                    <Ionicons name="time-outline" size={16} color={theme.colors.textSecondary} />
                    <Text style={[styles.thresholdLabel, { color: theme.colors.textSecondary }]}>
                      {isArabic ? 'تأخير:' : 'Debounce:'}
                    </Text>
                    <Text style={[styles.thresholdValue, { color: theme.colors.text }]}>
                      {rule.debounceSec}s
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}
      </ScrollView>

      {/* Templates Modal */}
      <Modal
        visible={showTemplatesModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowTemplatesModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {isArabic ? 'القوالب الجاهزة' : 'Templates'}
              </Text>
              <TouchableOpacity onPress={() => setShowTemplatesModal(false)}>
                <Ionicons name="close" size={24} color={theme.colors.text} />
              </TouchableOpacity>
            </View>

            <ScrollView>
              {templates.map((template) => (
                <TouchableOpacity
                  key={template.id}
                  style={[
                    styles.templateCard,
                    { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
                  ]}
                  onPress={() => applyTemplate(template.id)}
                >
                  <View style={styles.templateHeader}>
                    <Text style={[styles.templateName, { color: theme.colors.text }]}>
                      {isArabic ? template.nameAr : template.name}
                    </Text>
                    <View
                      style={[
                        styles.templateBadge,
                        { backgroundColor: theme.colors.primary + '20' },
                      ]}
                    >
                      <Text style={[styles.templateBadgeText, { color: theme.colors.primary }]}>
                        {template.rules.length} {isArabic ? 'قواعد' : 'rules'}
                      </Text>
                    </View>
                  </View>
                  <Text style={[styles.templateDescription, { color: theme.colors.textSecondary }]}>
                    {isArabic ? template.descriptionAr : template.description}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    margin: 16,
    borderRadius: 16,
  },
  deviceName: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  rulesCount: {
    fontSize: 14,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 12,
    gap: 6,
  },
  addButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 80,
  },
  emptyText: {
    fontSize: 16,
    marginTop: 16,
    marginBottom: 24,
  },
  emptyButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
  },
  emptyButtonText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: '600',
  },
  rulesList: {
    padding: 16,
    gap: 12,
  },
  ruleCard: {
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
  },
  ruleHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  ruleInfo: {
    flex: 1,
    gap: 8,
  },
  severityBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  severityText: {
    fontSize: 11,
    fontWeight: '700',
  },
  ruleType: {
    fontSize: 16,
    fontWeight: '600',
  },
  deleteButton: {
    padding: 12,
    marginLeft: 8,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 0, 0, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  ruleDetails: {
    gap: 8,
  },
  threshold: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  thresholdLabel: {
    fontSize: 14,
  },
  thresholdValue: {
    fontSize: 14,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    paddingTop: 16,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(128, 128, 128, 0.2)',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  templateCard: {
    margin: 16,
    marginTop: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  templateHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  templateName: {
    fontSize: 16,
    fontWeight: '600',
    flex: 1,
  },
  templateBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  templateBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  templateDescription: {
    fontSize: 14,
    lineHeight: 20,
  },
});
